import React from 'react';
import { Card, CardContent, Typography, Box, Tooltip, Divider } from '@mui/material';
import { motion } from 'framer-motion';

const categoryColors = {
  'Core CS':       '#3B82F6',
  'Math & Theory': '#8B5CF6',
  'Signals & EE':  '#EAB308',
  'Systems':       '#F97316',
  'ML & AI':       '#10B981',
  'Other':         '#9CA3AF',
};

const schools = [
  {
    title: 'UC Berkeley',
    description: 'B.S. Electrical Engineering and Computer Science',
    details: 'Regents Scholar, 2020–2024',
    coursework: [
      { dept: 'CS',     number: '61A',  shortName: 'Prog. Abstractions', title: 'Structure and Interpretation of Computer Programs', description: 'Introduction to CS — managing complexity through abstraction.', category: 'Core CS' },
      { dept: 'CS',     number: '61B',  shortName: 'Data Structures',    title: 'Data Structures', description: "Software and machines from a programmer's point of view.", category: 'Core CS' },
      { dept: 'CS',     number: '61C',  shortName: 'Architecture',       title: 'Computer Architecture', description: 'Machine language, computer organization, caches, performance, parallelism, CPU design.', category: 'Core CS' },
      { dept: 'EECS',   number: '16A',  shortName: 'Info. Devices I',    title: 'Designing Information Devices and Systems I', description: 'Linear algebra applied to imaging, signals and systems, machine learning, and other modern areas.', category: 'Signals & EE' },
      { dept: 'EECS',   number: '16B',  shortName: 'Info. Devices II',   title: 'Designing Information Devices and Systems II', description: 'Differential equations, frequency domain, stability and controllability, linear algebra for ML.', category: 'Signals & EE' },
      { dept: 'CS',     number: '70',   shortName: 'Discrete Math',      title: 'Discrete Mathematics and Probability Theory', description: 'Propositions, proofs, induction, arithmetic algorithms, probability, diagonalization, and computability.', category: 'Math & Theory' },
      { dept: 'CS',     number: '170',  shortName: 'Algorithms',         title: 'Efficient Algorithms and Intractable Problems', description: 'Graph algorithms, greedy algorithms, dynamic programming, LP, FFT, number theory, NP-completeness.', category: 'Math & Theory' },
      { dept: 'EECS',   number: '126',  shortName: 'Probability',        title: 'Probability and Random Processes', description: 'Probability, Markov Chains, queueing, MLE/MAP, Kalman filtering, information theory, communication channels.', category: 'Math & Theory' },
      { dept: 'EECS',   number: '127',  shortName: 'Optimization',       title: 'Optimization Models in Engineering', description: 'Optimization for ML, statistics, decision-making, and control, with emphasis on numerically tractable problems.', category: 'Math & Theory' },
      { dept: 'CS',     number: '162',  shortName: 'Operating Sys.',     title: 'Operating Systems and Systems Programming', description: 'Processes, IPC, synchronization, memory allocation, scheduling, file systems, basic networking.', category: 'Systems' },
      { dept: 'CS',     number: '161',  shortName: 'Security',           title: 'Computer Security', description: 'Cryptography, authentication, hash functions, OS security, access control, network security, firewalls.', category: 'Systems' },
      { dept: 'CS',     number: '168',  shortName: 'Networking',         title: 'Introduction to the Internet: Architecture and Protocols', description: 'Fundamental concepts of networking as embodied in the Internet.', category: 'Systems' },
      { dept: 'CS',     number: '189',  shortName: 'Machine Learning',   title: 'Introduction to Machine Learning', description: 'Regression, GDA, decision trees, neural networks, boosting, density estimation, dimensionality reduction, clustering.', category: 'ML & AI' },
      { dept: 'CS',     number: '182',  shortName: 'Deep Learning',      title: 'Designing, Visualizing, and Understanding Deep Neural Networks', description: 'Training and fine-tuning networks, meta-learning, transformers, generative models, stable diffusion.', category: 'ML & AI' },
      { dept: 'CogSci', number: '131',  shortName: 'Comp. Cognition',    title: 'Computational Models of Cognition', description: 'Models of human cognition — symbolic approaches, neural networks, probability and statistics.', category: 'ML & AI' },
      { dept: 'SCAND',  number: '1–4',  shortName: 'Swedish',            title: 'Swedish 1–4', description: 'Inte den mest relevanta på denna lista men... Svenska nivå B2 — arbetsförmåga', category: 'Other' },
    ],
  },
  {
    title: 'Lund University',
    description: 'Study abroad in Lund, Sweden',
    details: 'Spring 2023',
    coursework: [
      { dept: 'Lund', number: 'AI',  shortName: 'Artificial Intel.', title: 'Artificial Intelligence', description: 'Intelligent agents, game programming, knowledge-based systems, natural language processing.', category: 'ML & AI' },
      { dept: 'Lund', number: 'LC',  shortName: 'Learning Control',  title: 'Learning-Based Control', description: 'Theory and methodology for developing control laws based on measured input and output signal data.', category: 'ML & AI' },
    ],
  },
];

function ElementTile({ course, color }) {
  return (
    <Tooltip
      title={
        <Box sx={{ p: 0.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{course.title}</Typography>
          <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>{course.description}</Typography>
        </Box>
      }
      arrow
      placement="top"
      componentsProps={{
        tooltip: {
          sx: {
            maxWidth: 260,
            bgcolor: '#1e293b',
            '& .MuiTooltip-arrow': { color: '#1e293b' },
          },
        },
      }}
    >
      <motion.div
        whileHover={{ y: -4 }}
        transition={{ duration: 0.15 }}
        style={{ cursor: 'default' }}
      >
        <Box sx={{
          width:  { xs: 76, sm: 88 },
          height: { xs: 86, sm: 100 },
          border: '1px solid #e0e0e0',
          borderTop: `4px solid ${color}`,
          borderRadius: '4px',
          bgcolor: 'white',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 0.75,
          userSelect: 'none',
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          transition: 'box-shadow 0.15s',
          '&:hover': { boxShadow: `0 6px 20px ${color}33` },
        }}>
          <Typography sx={{ fontSize: '0.55rem', fontWeight: 700, color, letterSpacing: '0.04em', alignSelf: 'flex-start', lineHeight: 1 }}>
            {course.dept}
          </Typography>
          <Typography sx={{ fontSize: { xs: '1.2rem', sm: '1.45rem' }, fontWeight: 800, color: '#1a1a1a', lineHeight: 1, letterSpacing: '-0.02em' }}>
            {course.number}
          </Typography>
          <Typography sx={{ fontSize: '0.5rem', color: '#888', textAlign: 'center', lineHeight: 1.3 }}>
            {course.shortName}
          </Typography>
        </Box>
      </motion.div>
    </Tooltip>
  );
}

function CourseworkGrid({ courses }) {
  const grouped = {};
  courses.forEach(c => {
    if (!grouped[c.category]) grouped[c.category] = [];
    grouped[c.category].push(c);
  });

  return (
    <Box>
      {/* Legend */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2.5 }}>
        {Object.entries(categoryColors)
          .filter(([cat]) => grouped[cat])
          .map(([cat, color]) => (
            <Box key={cat} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: color, flexShrink: 0 }} />
              <Typography variant="caption" sx={{ color: '#666', fontSize: '0.65rem' }}>{cat}</Typography>
            </Box>
          ))}
      </Box>

      {/* Tiles by category */}
      {Object.entries(categoryColors)
        .filter(([cat]) => grouped[cat])
        .map(([category, color]) => (
          <Box key={category} sx={{ mb: 2.5 }}>
            <Typography variant="overline" sx={{ fontSize: '0.6rem', color, fontWeight: 700, letterSpacing: '0.1em', display: 'block', mb: 1 }}>
              {category}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {grouped[category].map((course, idx) => (
                <motion.div
                  key={course.number}
                  initial={{ opacity: 0, scale: 0.85 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.25, delay: idx * 0.04 }}
                >
                  <ElementTile course={course} color={color} />
                </motion.div>
              ))}
            </Box>
          </Box>
        ))}
    </Box>
  );
}

function Education() {
  return (
    <section id="education" style={{ paddingTop: '80px' }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
      >
        <Typography variant="h4" component="h2" gutterBottom>
          Education
        </Typography>
      </motion.div>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {schools.map((school, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
          >
            <Card>
              <CardContent>
                <Typography variant="h5" component="h3">{school.title}</Typography>
                <Typography variant="body2" color="textSecondary">{school.description}</Typography>
                <Divider sx={{ my: 1 }} />
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2.5 }}>{school.details}</Typography>
                <CourseworkGrid courses={school.coursework} />
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </Box>
    </section>
  );
}

export default Education;
